import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// (useState is imported below in React import)
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { CANTIERE } from "@/constants/testIds";
import { Plus, HardHat, Search, Receipt, Calculator, Trash2, Pencil } from "lucide-react";
import EliminaCantiereModal from "@/pages/cantiere/EliminaCantiereModal";
import { toast } from "sonner";
import SimulatoreGuadagno from "@/pages/cantiere/SimulatoreGuadagno";
import BollaFormModal from "@/pages/bolle/BollaFormModal";

const LAVORAZIONI_INIZIALI = [
  "Approntamento",
  "Demolizioni",
  "Costruzioni",
  "Impianto elettrico",
  "Impianto idraulico",
  "Impianto condizionamento",
  "Tinteggiature",
];

const nuovoPianoFatturazione = () =>
  Array.from({ length: 10 }, (_, i) => ({
    voce_id: `pf_${i + 1}`,
    descrizione: "",
    percentuale: "",
    collegamento_nome: "",
    stato: "da_maturare",
  }));

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TIPOLOGIE_CONTRATTO_IVA = [
  { id: "privato_22", label: "Privato 22%", cliente: "privato", iva: 22, ritenuta: 0 },
  { id: "privato_10", label: "Privato 10%", cliente: "privato", iva: 10, ritenuta: 0 },
  { id: "privato_10_ritenuta", label: "Privato 10% con ritenuta", cliente: "privato", iva: 10, ritenuta: 11 },
  { id: "condominio_10", label: "Condominio 10%", cliente: "condominio", iva: 10, ritenuta: 0 },
  { id: "condominio_10_ritenuta", label: "Condominio 10% con ritenuta", cliente: "condominio", iva: 10, ritenuta: 4 },
  { id: "azienda_22", label: "Azienda 22%", cliente: "azienda", iva: 22, ritenuta: 0 },
];

const calcolaContrattoIVA = (lordo, tipologiaId) => {
  const tipo = TIPOLOGIE_CONTRATTO_IVA.find((t) => t.id === tipologiaId);
  if (!tipo) return null;

  const totale = Number(lordo || 0);
  const imponibile = Math.round((totale / (1 + tipo.iva / 100)) * 100) / 100;
  const iva = Math.round((totale - imponibile) * 100) / 100;
  const ritenuta = tipo.ritenuta
    ? Math.round(((totale / 1.22) * tipo.ritenuta / 100) * 100) / 100
    : 0;

  return {
    imponibile,
    iva,
    lordo: totale,
    ritenuta,
    incassoCantiere: Math.round((totale - ritenuta) * 100) / 100,
  };
};

const STATO_LABEL = {
  preventivo: "Preventivo",
  attivo: "Attivo",
  sospeso: "Sospeso",
  completato: "Completato",
  annullato: "Annullato",
};
const STATO_COLOR = {
  preventivo: "bg-blue-100 text-blue-800 border-blue-200",
  attivo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  sospeso: "bg-yellow-100 text-yellow-800 border-yellow-200",
  completato: "bg-slate-100 text-slate-800 border-slate-200",
  annullato: "bg-red-100 text-red-800 border-red-200",
};

export default function Cantieri({ tipo = "cantiere", label = "Cantieri", labelSingolare = "Cantiere", articoloIndet = "Nuovo" } = {}) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // cantiere_id in modifica, null = creazione
  const [simulatoreOpen, setSimulatoreOpen] = useState(false);
  const [query, setQuery] = useState("");

  const queryKey = tipo === "manutenzione" ? ["manutenzioni"] : ["cantieri"];
  const listParams = tipo === "manutenzione" ? { tipo: "manutenzione" } : {};
  const [quickBolla, setQuickBolla] = useState(null);

  const { data: cantieri = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get("/cantieri", { params: listParams }).then((r) => r.data),
  });

  // Attività reali del cronoprogramma:
  // usate per collegare le quote di fatturazione tramite attivita_id.
  const { data: attivitaPiano = [] } = useQuery({
    queryKey: ["attivita-piano-fatturazione", editingId],
    queryFn: () =>
      api
        .get("/cronoprogramma", {
          params: { cantiere_id: editingId },
        })
        .then((r) => r.data),
    enabled: !!editingId && modalOpen,
  });

  const emptyForm = {
    nome: "",
    cliente: "",
    tipo_cliente: "privato",
    tipologia_contratto_iva: "privato_22",
    aliquota_iva_privato: 22,
    importo_contratto_lordo_iva: 0,
    aliquota_ritenuta: 0,
    importo_ritenuta: 0,
    commessa: "",
    indirizzo: "",
    referente: "",
    responsabile: user?.name || "",
    stato: "attivo",
    data_inizio: "",
    data_fine_prevista: "",
    importo_contratto: 0,
    piano_fatturazione: nuovoPianoFatturazione(),
    lat: "",
    lng: "",
    raggio_metri: 200,
    spese_generali_percentuale: 8,
  };
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, responsabile: user?.name || "" });
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.cantiere_id);
    setForm({
      nome: c.nome || "",
      cliente: c.cliente || "",
      tipo_cliente: c.tipo_cliente || "privato",
      // null identifica i cantieri storici: non scorporare nuovamente l'IVA.
      tipologia_contratto_iva: c.tipologia_contratto_iva || null,
      contratto_storico: !c.tipologia_contratto_iva && c.importo_contratto_lordo_iva == null,
      aliquota_iva_privato: c.aliquota_iva_privato ?? 22,
      importo_contratto_lordo_iva: c.importo_contratto_lordo_iva ?? null,
      aliquota_ritenuta: c.aliquota_ritenuta ?? 0,
      importo_ritenuta: c.importo_ritenuta ?? 0,
      commessa: c.commessa || "",
      indirizzo: c.indirizzo || "",
      referente: c.referente || "",
      responsabile: c.responsabile || "",
      stato: c.stato || "attivo",
      data_inizio: c.data_inizio || "",
      data_fine_prevista: c.data_fine_prevista || "",
      importo_contratto: c.importo_contratto || 0,
      lat: c.lat ?? "",
      lng: c.lng ?? "",
      raggio_metri: c.raggio_metri ?? 200,
      spese_generali_percentuale: c.spese_generali_percentuale ?? 8,
      piano_fatturazione:
        Array.isArray(c.piano_fatturazione) && c.piano_fatturazione.length
          ? [
              ...c.piano_fatturazione,
              ...nuovoPianoFatturazione(),
            ].slice(0, 10)
          : nuovoPianoFatturazione(),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const create = useMutation({
    mutationFn: (payload) => api.post("/cantieri", { ...payload, tipo }).then((r) => r.data),
    onSuccess: () => {
      toast.success(`${labelSingolare} creato`);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      closeModal();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const update = useMutation({
    mutationFn: ({ cantiere_id, payload }) =>
      api.patch(`/cantieri/${cantiere_id}`, { ...payload, tipo }).then((r) => r.data),
    onSuccess: () => {
      toast.success(`${labelSingolare} aggiornato`);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ"] });
      closeModal();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore aggiornamento"),
  });

  const submit = () => {
    const { contratto_storico, ...datiCantiere } = form;
    const payload = {
      ...datiCantiere,
      piano_fatturazione: (form.piano_fatturazione || [])
        .filter((r) =>
          (r.descrizione || "").trim() ||
          Number(r.percentuale || 0) > 0 ||
          (r.collegamento_nome || "").trim()
        )
        .map((r, idx) => ({
          ...r,
          voce_id: r.voce_id || `pf_${idx + 1}`,
          descrizione: (r.descrizione || "").trim(),
          percentuale: Number(r.percentuale || 0),
          collegamento_nome: (r.collegamento_nome || "").trim(),
          stato: r.stato || "da_maturare",
        })),
      lat: form.lat === "" || form.lat === null ? null : parseFloat(form.lat),
      lng: form.lng === "" || form.lng === null ? null : parseFloat(form.lng),
    };
    if (editingId) {
      update.mutate({ cantiere_id: editingId, payload });
    } else {
      create.mutate(payload);
    }
  };

  const removeCantiere = useMutation({
    mutationFn: (cantiere_id) => api.delete(`/cantieri/${cantiere_id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success(`${labelSingolare} spostato nel cestino — ripristinabile da Backup · Log · Cestino`);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore eliminazione"),
  });
  const [eliminaFor, setEliminaFor] = useState(null);

  const filtered = cantieri.filter(
    (c) =>
      c.nome.toLowerCase().includes(query.toLowerCase()) ||
      c.cliente.toLowerCase().includes(query.toLowerCase()) ||
      c.commessa.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid={CANTIERE.list}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">{label}</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Gestione {label.toLowerCase()}</h1>
          <p className="text-slate-600 text-sm mt-1">
            {cantieri.length} {cantieri.length === 1 ? labelSingolare.toLowerCase() : label.toLowerCase()} total{cantieri.length === 1 ? "e" : "i"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca..."
              className="pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-sm text-sm w-56"
            />
          </div>
          {admin && (
            <button
              onClick={() => setSimulatoreOpen(true)}
              data-testid="btn-simulatore-guadagno"
              title="Simula guadagno atteso prima di aprire il cantiere"
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-violet-500 text-violet-700 hover:bg-violet-50 font-semibold rounded-sm text-sm"
            >
              <Calculator className="w-4 h-4" /> Simulatore
            </button>
          )}
          {admin && (
            <button
              onClick={openCreate}
              data-testid={CANTIERE.newButton}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm text-sm shadow-brutal"
            >
              <Plus className="w-4 h-4" /> {articoloIndet} {labelSingolare.toLowerCase()}
            </button>
          )}
        </div>
      </div>

      <SimulatoreGuadagno open={simulatoreOpen} onClose={() => setSimulatoreOpen(false)} />

      {isLoading ? (
        <div className="text-slate-500">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
          <HardHat className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500">Nessun{articoloIndet === "Nuova" ? "a" : ""} {labelSingolare.toLowerCase()} trovat{articoloIndet === "Nuova" ? "a" : "o"}</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.cantiere_id} className="relative">
              <Link
                to={`/cantieri/${c.cantiere_id}`}
                data-testid={CANTIERE.card}
                className="block bg-white border border-slate-200 p-5 hover:border-slate-900 transition-colors shadow-brutal"
              >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    {c.commessa}
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight">{c.nome}</h3>
                </div>
                <span
                  className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 border rounded-sm ${STATO_COLOR[c.stato] || ""}`}
                >
                  {STATO_LABEL[c.stato] || c.stato}
                </span>
              </div>
              <div className="text-sm text-slate-600 mb-3">{c.cliente}</div>
              <div className="text-xs text-slate-500 mb-3">{c.indirizzo}</div>
              {admin && (
                <div className="border-t border-slate-100 pt-3 mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="overline text-[9px]">Contratto</div>
                    <div className="font-display font-bold">{eur(c.importo_contratto)}</div>
                  </div>
                  <div>
                    <div className="overline text-[9px]">Fatturato</div>
                    <div className="font-display font-bold">{eur(c.importo_fatturato)}</div>
                  </div>
                </div>
              )}
              <div className="mt-3">
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500" style={{ width: `${c.percentuale_avanzamento_lavori}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Avanzamento lavori: {c.percentuale_avanzamento_lavori}%
                </div>
              </div>
            </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuickBolla({ cantiere_id: c.cantiere_id, cantiere_nome: c.nome });
                }}
                data-testid={`btn-bolla-veloce-${c.cantiere_id}`}
                title={`Nuova bolla per ${c.nome}`}
                className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-orange-100 border border-slate-300 text-slate-700 hover:text-orange-800 rounded-sm shadow-sm"
              >
                <Receipt className="w-4 h-4" />
              </button>
              {admin && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openEdit(c);
                  }}
                  data-testid={`btn-modifica-${c.cantiere_id}`}
                  title={`Modifica ${labelSingolare.toLowerCase()} — nome, cliente, indirizzo, contratto, ecc.`}
                  className="absolute top-2 right-10 p-1.5 bg-white/90 hover:bg-blue-100 border border-slate-300 text-slate-700 hover:text-blue-700 rounded-sm shadow-sm"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {admin && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEliminaFor(c);
                  }}
                  data-testid={`btn-elimina-${c.cantiere_id}`}
                  title={`Elimina ${labelSingolare.toLowerCase()} (scegli archivia o definitivo)`}
                  disabled={removeCantiere.isPending}
                  className="absolute top-2 right-[4.5rem] p-1.5 bg-white/90 hover:bg-rose-100 border border-slate-300 text-slate-700 hover:text-rose-700 rounded-sm shadow-sm disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {quickBolla && (
        <BollaFormModal
          open={!!quickBolla}
          onClose={() => setQuickBolla(null)}
          onSaved={() => {
            toast.success("Bolla registrata — visibile in Contabilità");
            setQuickBolla(null);
          }}
          cantieri={cantieri}
          initialCantiereId={quickBolla.cantiere_id}
        />
      )}

      {eliminaFor && (
        <EliminaCantiereModal
          cantiere={eliminaFor}
          tipoLabel={labelSingolare.toLowerCase()}
          onClose={() => setEliminaFor(null)}
        />
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div
            className="bg-white w-full max-w-2xl border border-slate-200 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={editingId ? "modal-modifica-cantiere" : "modal-nuovo-cantiere"}
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">
                {editingId ? `Modifica ${labelSingolare.toLowerCase()}` : `${articoloIndet} ${labelSingolare.toLowerCase()}`}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-4">
              {[
                ["nome", "Nome cantiere *"],
                ["cliente", "Cliente *"],
                ["commessa", "Commessa *"],
                ["indirizzo", "Indirizzo"],
                ["referente", "Referente cliente"],
                ["responsabile", "Responsabile cantiere"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">{l}</label>
                  <input
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">
                  Tipo cliente <span className="text-rose-600">*</span>
                  <span className="ml-2 text-[10px] text-slate-500 font-normal">
                    auto-compila l'aliquota IVA dei rapportini di questo cantiere
                  </span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {TIPOLOGIE_CONTRATTO_IVA.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      title={editingId && !form.tipologia_contratto_iva
                        ? "Imposta IVA e ritenuta senza modificare il contratto storico"
                        : t.label}
                      onClick={() => {
                        const calcolo = calcolaContrattoIVA(
                          form.importo_contratto_lordo_iva,
                          t.id
                        );
                        setForm({
                          ...form,
                          tipologia_contratto_iva: t.id,
                          tipo_cliente: t.cliente,
                          aliquota_iva_privato: t.cliente === "privato" ? t.iva : 22,
                          aliquota_ritenuta: t.ritenuta,
                          ...(calcolo && form.importo_contratto_lordo_iva != null
                            ? {
                                importo_contratto: calcolo.imponibile,
                                importo_ritenuta: calcolo.ritenuta,
                              }
                            : {}),
                        });
                      }}
                      data-testid={`cant-tipologia-iva-${t.id}`}
                      className={`px-3 py-3 text-xs font-bold uppercase tracking-wide border-2 rounded-sm transition-all ${
                        form.tipologia_contratto_iva === t.id
                          ? "border-orange-500 bg-orange-50 text-orange-900 ring-2 ring-orange-300"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Data inizio</label>
                <input
                  type="date"
                  value={form.data_inizio}
                  onChange={(e) => setForm({ ...form, data_inizio: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Data fine prevista</label>
                <input
                  type="date"
                  value={form.data_fine_prevista}
                  onChange={(e) => setForm({ ...form, data_fine_prevista: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">
                  {form.contratto_storico
                    ? "Importo contratto € (storico, IVA esclusa)"
                    : form.tipologia_contratto_iva
                      ? "Importo contratto IVA inclusa €"
                      : "Importo contratto € (storico, IVA esclusa)"}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.contratto_storico
                    ? form.importo_contratto
                    : form.tipologia_contratto_iva
                      ? (form.importo_contratto_lordo_iva ?? "")
                      : form.importo_contratto}
                  onChange={(e) => {
                    const valore = Number(e.target.value || 0);
                    if (form.contratto_storico || !form.tipologia_contratto_iva) {
                      setForm({ ...form, importo_contratto: valore });
                      return;
                    }
                    const calcolo = calcolaContrattoIVA(valore, form.tipologia_contratto_iva);
                    setForm({
                      ...form,
                      importo_contratto_lordo_iva: valore,
                      importo_contratto: calcolo.imponibile,
                      importo_ritenuta: calcolo.ritenuta,
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              {form.tipologia_contratto_iva && !form.contratto_storico && (() => {
                const c = calcolaContrattoIVA(
                  form.importo_contratto_lordo_iva,
                  form.tipologia_contratto_iva
                );
                if (!c) return null;
                return (
                  <div className="md:col-span-2 border border-orange-200 bg-orange-50 p-4 rounded-sm">
                    <div className="text-xs font-black uppercase tracking-widest text-orange-900 mb-3">
                      Riepilogo IVA e ritenuta
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div><span className="text-slate-600">Imponibile</span><div className="font-bold">{eur(c.imponibile)}</div></div>
                      <div><span className="text-slate-600">IVA a debito</span><div className="font-bold">{eur(c.iva)}</div></div>
                      <div><span className="text-slate-600">Totale IVA inclusa</span><div className="font-bold">{eur(c.lordo)}</div></div>
                      <div><span className="text-slate-600">Ritenuta</span><div className="font-bold">{eur(c.ritenuta)}</div></div>
                      <div><span className="text-slate-600">Incasso previsto</span><div className="font-bold text-emerald-700">{eur(c.incassoCantiere)}</div></div>
                    </div>
                  </div>
                );
              })()}
              <div className="md:col-span-2 border border-slate-300 bg-slate-50 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-black uppercase tracking-widest text-slate-800">
                      Piano di fatturazione
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Fino a 10 scadenze. Descrizione, percentuale e lavorazione
                      collegata sono completamente modificabili per ogni cantiere.
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Totale
                    </div>
                    <div
                      className={`font-mono font-black ${
                        Math.abs(
                          (form.piano_fatturazione || []).reduce(
                            (sum, r) => sum + Number(r.percentuale || 0),
                            0
                          ) - 100
                        ) < 0.001
                          ? "text-emerald-700"
                          : "text-amber-700"
                      }`}
                    >
                      {(form.piano_fatturazione || []).reduce(
                        (sum, r) => sum + Number(r.percentuale || 0),
                        0
                      ).toFixed(2)}%
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {(form.piano_fatturazione || []).map((r, idx) => {
                    const importo =
                      Number(form.importo_contratto || 0) *
                      Number(r.percentuale || 0) /
                      100;

                    return (
                      <div
                        key={r.voce_id || idx}
                        className="grid grid-cols-12 gap-2 items-end bg-white border border-slate-200 p-2"
                      >
                        <div className="col-span-12 md:col-span-4">
                          <label className="text-[10px] uppercase font-bold text-slate-500">
                            {idx + 1}. Descrizione pagamento
                          </label>
                          <input
                            value={r.descrizione || ""}
                            onChange={(e) => {
                              const rows = [...form.piano_fatturazione];
                              rows[idx] = {
                                ...rows[idx],
                                descrizione: e.target.value,
                              };
                              setForm({
                                ...form,
                                piano_fatturazione: rows,
                              });
                            }}
                            placeholder="Es. Acconto fine demolizioni"
                            className="w-full px-2 py-2 border border-slate-300 text-xs"
                          />
                        </div>

                        <div className="col-span-4 md:col-span-2">
                          <label className="text-[10px] uppercase font-bold text-slate-500">
                            Percentuale
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={r.percentuale ?? ""}
                              onChange={(e) => {
                                const rows = [...form.piano_fatturazione];
                                rows[idx] = {
                                  ...rows[idx],
                                  percentuale: e.target.value,
                                };
                                setForm({
                                  ...form,
                                  piano_fatturazione: rows,
                                });
                              }}
                              className="w-full px-2 py-2 border border-slate-300 text-xs"
                            />
                            <span className="px-2 py-2 bg-slate-100 border border-l-0 border-slate-300 text-xs font-bold">
                              %
                            </span>
                          </div>
                        </div>

                        <div className="col-span-8 md:col-span-4">
                          <label className="text-[10px] uppercase font-bold text-slate-500">
                            Lavorazione collegata
                          </label>
                          <select
                            value={
                              r.attivita_id_collegata
                                ? `id:${r.attivita_id_collegata}`
                                : r.collegamento_nome
                                  ? `nome:${r.collegamento_nome}`
                                  : ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              const rows = [...form.piano_fatturazione];

                              if (!value) {
                                rows[idx] = {
                                  ...rows[idx],
                                  attivita_id_collegata: "",
                                  attivita_nome_collegata: "",
                                  collegamento_nome: "",
                                };
                              } else if (value.startsWith("id:")) {
                                const aid = value.slice(3);
                                const att = attivitaPiano.find(
                                  (a) => a.attivita_id === aid
                                );

                                rows[idx] = {
                                  ...rows[idx],
                                  attivita_id_collegata: aid,
                                  attivita_nome_collegata: att?.nome || "",
                                  collegamento_nome: att?.nome || "",
                                };
                              } else if (value.startsWith("nome:")) {
                                const nome = value.slice(5);

                                // Nuovo cantiere: l'ID verrà risolto dal backend
                                // immediatamente dopo la creazione del cronoprogramma.
                                rows[idx] = {
                                  ...rows[idx],
                                  attivita_id_collegata: "",
                                  attivita_nome_collegata: nome,
                                  collegamento_nome: nome,
                                };
                              }

                              setForm({
                                ...form,
                                piano_fatturazione: rows,
                              });
                            }}
                            className="w-full px-2 py-2 border border-slate-300 text-xs bg-white"
                          >
                            <option value="">— Nessun collegamento —</option>

                            {editingId
                              ? attivitaPiano.map((a) => (
                                  <option
                                    key={a.attivita_id}
                                    value={`id:${a.attivita_id}`}
                                  >
                                    {a.nome}
                                    {a.percentuale_avanzamento != null
                                      ? ` · ${Number(a.percentuale_avanzamento).toFixed(0)}%`
                                      : ""}
                                  </option>
                                ))
                              : LAVORAZIONI_INIZIALI.map((nome) => (
                                  <option
                                    key={nome}
                                    value={`nome:${nome}`}
                                  >
                                    {nome}
                                  </option>
                                ))}
                          </select>

                          {r.attivita_id_collegata && (
                            <div className="mt-1 text-[9px] text-emerald-700 font-mono truncate">
                              Collegato: {r.attivita_id_collegata}
                            </div>
                          )}
                        </div>

                        <div className="col-span-12 md:col-span-2 text-right">
                          <div className="text-[10px] uppercase text-slate-400">
                            Importo
                          </div>
                          <div className="font-mono font-bold text-xs text-slate-800">
                            € {importo.toLocaleString("it-IT", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-[11px] text-slate-500">
                  Quando la lavorazione collegata viene impostata come
                  <b> Eseguita</b>, il sistema genera automaticamente la richiesta
                  di fatturazione e invia l'alert all'amministrazione.
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Spese generali %</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.spese_generali_percentuale}
                  onChange={(e) => setForm({ ...form, spese_generali_percentuale: parseFloat(e.target.value || 0) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Latitudine</label>
                <input
                  type="number"
                  step="0.000001"
                  value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Longitudine</label>
                <input
                  type="number"
                  step="0.000001"
                  value={form.lng}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Raggio autorizzato (m)</label>
                <input
                  type="number"
                  value={form.raggio_metri}
                  onChange={(e) => setForm({ ...form, raggio_metri: parseInt(e.target.value || 0, 10) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={closeModal} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">
                Annulla
              </button>
              <button
                data-testid={CANTIERE.saveButton}
                onClick={submit}
                disabled={create.isPending || update.isPending}
                className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
              >
                {create.isPending || update.isPending
                  ? "Salvataggio…"
                  : editingId ? `Salva modifiche` : `Salva ${labelSingolare.toLowerCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============================================================================
 * (Il vecchio QuickBollaModal è stato rimosso in favore di BollaFormModal —
 *  vedi /pages/bolle/BollaFormModal.jsx. Questo garantisce che il format bolla
 *  aperto tramite l'icona € nei listati Cantieri/Manutenzioni sia identico a
 *  quello della pagina Bolle principale e resti automaticamente sincronizzato
 *  a ogni modifica futura del form.)
 * ============================================================================ */
